import React from 'react';
import {
    DefaultButton,
    Icon,
    Link,
    MessageBar,
    MessageBarType,
    SearchBox,
    Spinner,
    SpinnerSize,
    Stack,
    Text,
    useTheme,
} from '@fluentui/react';
import CurrencyDisplay from '../../Currency/CurrencyDisplay';
import SuggestionReasonChip from '../SuggestionReasonChip';
import ProductSearchBox from '../../Search/ProductSearchBox';
import {useLazyGetEmbeddedSearchResultsQuery} from '../../../store/Api';
import {useLazySearchAdminStorefrontBundlesQuery} from '../../../store/endpoints/StorefrontSettingsEndpoints';
import {ISearchRequest} from '../../../models/Search';
import {IStorefrontSuggestedBundle, IStorefrontSuggestedProduct} from '../../../models/Storefront';
import {ISuggestionReason} from '../suggestedProductsLogic';
import {IBundleAvailability} from '../suggestedBundlesLogic';
import {
    emptyWizardCriteria,
    filterSuggestedBundles,
    IWizardBundleRow,
    IWizardProductRow,
    partitionBundleResults,
    partitionProductResults,
    suggestedBundleRow,
    suggestedProductRow,
    WIZARD_SEARCH_DEBOUNCE_MS,
    WIZARD_SEARCH_PAGE_SIZE,
    wizardCriteriaIsEmpty,
} from './wizardSearchLogic';

interface ISuggestionRowProps {
    imageUrl?: string;
    name?: string;
    detail?: string;
    /** Absent on a catalog result, which was found rather than put forward and so has nothing to explain. */
    reason?: ISuggestionReason;
    /** Green sellable, amber unpriceable, red out of stock. Resolved against the theme in the row. */
    availabilityKind: IBundleAvailability['kind'];
    availabilityLabel: string;
    availabilityTooltip?: string;
    /** The cost, or the phrase to show in its place when there is not one. */
    cost?: number;
    costFallback?: string;
    added: boolean;
    pending: boolean;
    disabled: boolean;
    onOpen?: () => void;
    onAdd: () => void;
}

/**
 * One suggestion, as a row rather than a card.
 *
 * The strips on the storefront tabs lay these out as cards because they sit across a full-width page; in a
 * dialog the same information reads better stacked, and a row lets a dozen of them be scanned without
 * scrolling sideways. Everything said here is said by the same helpers the cards use, so a product means the
 * same thing in both places.
 */
const SuggestionRow: React.FC<ISuggestionRowProps> = (props) => {
    const theme = useTheme();
    const dotColor = props.availabilityKind === 'inStock'
        ? theme.palette.green
        : props.availabilityKind === 'unpriced' ? theme.palette.orange : theme.palette.red;
    return (
        <Stack
            horizontal
            verticalAlign='center'
            tokens={{childrenGap: '0.5em'}}
            styles={{root: {
                padding: '6px 4px',
                borderBottom: `1px solid ${theme.palette.neutralLighter}`,
            }}}
        >
            {/* Dropped rather than replaced with a placeholder tile when there is no image: a column of
              * identical grey squares says nothing and the text is better off with the width. */}
            {props.imageUrl && (
                <img
                    src={props.imageUrl}
                    alt=''
                    onClick={props.onOpen}
                    style={{
                        width: 40,
                        height: 40,
                        flexShrink: 0,
                        objectFit: 'contain',
                        borderRadius: 4,
                        cursor: props.onOpen ? 'pointer' : undefined,
                    }}
                />
            )}

            <Stack grow styles={{root: {minWidth: 0}}}>
                <Link
                    onClick={props.onOpen}
                    disabled={!props.onOpen}
                    title={props.name}
                    styles={{root: {
                        fontWeight: 600,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        display: 'block',
                    }}}
                >
                    {props.name ?? 'Untitled'}
                </Link>
                {props.detail && (
                    <Text
                        variant='small'
                        block
                        nowrap
                        title={props.detail}
                        styles={{root: {
                            color: theme.palette.neutralSecondary,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                        }}}
                    >
                        {props.detail}
                    </Text>
                )}
            </Stack>

            {/* The slot collapses rather than reserving width when there is nothing to say: the columns
              * that have to line up down the list are the fixed ones to the right of it. */}
            <Stack.Item styles={{root: {flexShrink: 0, maxWidth: 150}}}>
                {props.reason && <SuggestionReasonChip reason={props.reason}/>}
            </Stack.Item>

            {/* A dot and a word rather than a filled badge: availability is context here, not the headline. */}
            <Stack
                horizontal
                verticalAlign='center'
                tokens={{childrenGap: 4}}
                title={props.availabilityTooltip}
                styles={{root: {flexShrink: 0, width: 110, whiteSpace: 'nowrap'}}}
            >
                <span
                    style={{
                        width: 8,
                        height: 8,
                        flexShrink: 0,
                        borderRadius: '50%',
                        background: dotColor,
                    }}
                />
                <Text variant='small' nowrap styles={{root: {color: theme.palette.neutralSecondary}}}>
                    {props.availabilityLabel}
                </Text>
            </Stack>

            <Stack.Item styles={{root: {flexShrink: 0, width: 90, textAlign: 'right'}}}>
                {props.cost !== undefined ? (
                    <Text styles={{root: {fontWeight: 600}}} title='What this costs your organization'>
                        <CurrencyDisplay value={props.cost}/>
                    </Text>
                ) : (
                    <Text variant='small' styles={{root: {color: theme.palette.neutralSecondary}}}>
                        {props.costFallback}
                    </Text>
                )}
            </Stack.Item>

            <Stack.Item styles={{root: {flexShrink: 0, width: 90, textAlign: 'right'}}}>
                {props.added ? (
                    <Text variant='small' styles={{root: {color: theme.palette.themePrimary}}}>
                        <Icon iconName='CheckMark' styles={{root: {marginRight: 4}}}/>
                        Added
                    </Text>
                ) : (
                    <DefaultButton
                        text={props.pending ? 'Adding...' : 'Add'}
                        title={`Add ${props.name ?? 'this item'} to your storefront`}
                        disabled={props.disabled || props.pending}
                        onClick={props.onAdd}
                        styles={{root: {height: 28, minWidth: 0, padding: '0 12px', borderRadius: 4}}}
                    />
                )}
            </Stack.Item>
        </Stack>
    );
};

/** The shell every suggestions tab shares: the intro line, then a list, a spinner, or an explanation. */
const SuggestionsShell: React.FC<{
    intro: string;
    /**
     * The search field, between the intro and the list.
     *
     * Outside every gate below it on purpose: a field that vanished while its own results were loading,
     * or when a search found nothing, would take away the only way back out of that state.
     */
    search?: React.ReactNode;
    loading: boolean;
    loadingLabel: string;
    error: boolean;
    empty: boolean;
    emptyMessage: string;
}> = (props) => {
    const theme = useTheme();
    return (
        <Stack tokens={{childrenGap: '0.5em'}} styles={{root: {paddingTop: '1em', minWidth: 0}}}>
            <Text variant='small' styles={{root: {color: theme.palette.neutralSecondary}}}>
                {props.intro}
            </Text>

            {props.search}

            {props.loading && <Spinner size={SpinnerSize.medium} label={props.loadingLabel}/>}

            {!props.loading && props.error && (
                <MessageBar messageBarType={MessageBarType.error}>
                    We could not load suggestions right now.
                </MessageBar>
            )}

            {!props.loading && !props.error && props.empty && (
                <MessageBar messageBarType={MessageBarType.info}>{props.emptyMessage}</MessageBar>
            )}

            {/* No height cap and no scrollbar of its own: the step body it sits in owns the scrolling,
              * and a list that scrolled inside a scrolling panel would give the dialog two of them. */}
            {!props.loading && !props.error && !props.empty && (
                <Stack styles={{root: {minWidth: 0}}}>
                    {props.children}
                </Stack>
            )}
        </Stack>
    );
};

/**
 * The divider between the two halves of the one list.
 *
 * A heading rather than a second list: the operator is looking at one set of things they can shelve, and
 * splitting them into two scrolling regions would ask them to look in two places for the same Add button.
 */
const GroupHeading: React.FC<{text: string}> = (props) => {
    const theme = useTheme();
    return (
        <Text
            variant='small'
            block
            styles={{root: {
                paddingTop: '0.5em',
                fontWeight: 600,
                letterSpacing: '0.05em',
                textTransform: 'uppercase',
                color: theme.palette.neutralSecondary,
            }}}
        >
            {props.text}
        </Text>
    );
};

const SUGGESTED_GROUP = 'Suggested';
const CATALOG_GROUP = 'More from your catalog';

interface IWizardProductsTabProps {
    /** Every suggestion that was fetched, not just the ones shown. Searching covers the whole set. */
    products: IStorefrontSuggestedProduct[];
    /** How many of them to list before anyone searches. */
    displayCount: number;
    /** The catalog the ids belong to. Without it there is no bulk listing route to call. */
    catalog?: string;
    loading: boolean;
    error: boolean;
    addedIds: string[];
    /** An add is already running, here or on the bundles tab. */
    pendingId?: string;
    busy: boolean;
    onAdd: (product: IStorefrontSuggestedProduct) => void;
    onOpenDetails: (product: IStorefrontSuggestedProduct) => void;
}

/**
 * "Here is what to put on the shelves", and a way to go looking for the rest.
 *
 * The generation seeds the store with whatever the template carried, so everything offered here is
 * deliberately something the store does not already list. Adds are written immediately rather than staged:
 * the listing call is additive and idempotent, and a build that gets cancelled takes the whole storefront
 * with it, so there is nothing a staging step would protect.
 *
 * The search is the search page's own omnibox, chips and all, rather than a plain box: composing
 * `cat:Laptops ram=32gb` is exactly what an operator stocking a store wants to do, and a second, weaker
 * search language for the same catalog would be a thing to learn twice. It is mounted controlled, so the
 * criteria it composes are this step's alone and neither inherit from nor leak into the search page.
 *
 * Searching does not replace the suggestions, it re-ranks them: every fetched suggestion id rides along as
 * a boost, so the ones that match float to the top of the results and keep the chip that says why they were
 * put forward. What follows them under the second heading is the rest of the catalog.
 */
export const WizardProductsTab: React.FC<IWizardProductsTabProps> = (props) => {
    // The applied criteria, and only the applied ones. The box hydrates its chips from this, so it has to
    // hold still between applies; the keystrokes in flight live in the state below it instead.
    const [criteria, setCriteria] = React.useState<ISearchRequest>(emptyWizardCriteria);
    const [typedKeywords, setTypedKeywords] = React.useState('');
    const [runSearch, results] = useLazyGetEmbeddedSearchResultsQuery();
    // An apply is a decision, not a keystroke, so it skips the debounce. Set by onApply and read once.
    const applyImmediatelyRef = React.useRef(false);

    const {products, catalog, onOpenDetails} = props;
    const boostProductIds = React.useMemo(() => products.map((product) => product.id), [products]);
    const composed = React.useMemo<ISearchRequest>(
        () => ({...criteria, keywords: typedKeywords, boostProductIds}),
        [criteria, typedKeywords, boostProductIds]
    );
    const searching = !wizardCriteriaIsEmpty(composed);

    React.useEffect(() => {
        if (!searching) return;
        const fire = () => runSearch({catalog, criteria: composed});
        if (applyImmediatelyRef.current) {
            applyImmediatelyRef.current = false;
            fire();
            return;
        }
        const handle = setTimeout(fire, WIZARD_SEARCH_DEBOUNCE_MS);
        return () => clearTimeout(handle);
    }, [searching, composed, catalog, runSearch]);

    const applyCriteria = React.useCallback((next: ISearchRequest) => {
        applyImmediatelyRef.current = true;
        setCriteria(next);
        // The box clears or rewrites its own text on an apply, so the pending keystrokes go with it.
        setTypedKeywords(next.keywords ?? '');
    }, []);

    const groups = React.useMemo(
        () => partitionProductResults(results.data?.products ?? [], products),
        [results.data, products]
    );
    const shown = React.useMemo(
        () => products.slice(0, props.displayCount).map(suggestedProductRow),
        [products, props.displayCount]
    );

    // A picked quicksearch row opens the wizard's own details modal, never the search page's. Only for
    // something already on screen: opening details for a product the operator cannot see the row of would
    // leave them nowhere to go when they close it.
    const openPicked = React.useCallback((id: string) => {
        const row = [...groups.suggested, ...groups.catalog].find((entry) => entry.id === id);
        if (row && catalog) onOpenDetails(row.addTarget);
    }, [groups, catalog, onOpenDetails]);

    const renderRow = (row: IWizardProductRow) => (
        <SuggestionRow
            key={row.id}
            {...row}
            added={props.addedIds.includes(row.id)}
            pending={props.pendingId === row.id}
            disabled={props.busy || !props.catalog}
            onOpen={props.catalog ? () => props.onOpenDetails(row.addTarget) : undefined}
            onAdd={() => props.onAdd(row.addTarget)}
        />
    );

    // Full width of the step body: the omnibox sizes its suggestion flyout to the field, and a narrow
    // field would clip the attribute rows inside it.
    const searchBar = (
        <div style={{width: '100%', minWidth: 0}}>
            <ProductSearchBox
                catalogName={props.catalog}
                criteria={criteria}
                onKeywordChange={setTypedKeywords}
                onApply={applyCriteria}
                onProductSelected={openPicked}
            />
        </div>
    );

    const searchEmpty = groups.suggested.length === 0 && groups.catalog.length === 0;
    return (
        <SuggestionsShell
            intro='Here are some suggested products for your Storefront. Search to add anything else from your catalog.'
            search={searchBar}
            // Mid-search the spinner only takes over the list while there is nothing to show at all; a
            // refined query keeps the previous results on screen rather than blinking through a spinner.
            loading={searching ? (results.isUninitialized || (results.isFetching && !results.data)) : props.loading}
            loadingLabel={searching ? 'Searching your catalog...' : 'Looking for products to start you off...'}
            error={searching ? results.isError : props.error}
            empty={searching ? searchEmpty : shown.length === 0}
            emptyMessage={searching
                ? 'Nothing in your catalog matched that search.'
                : 'We could not find anything to start you off with. You can add products from the search page whenever you like.'}
        >
            {searching ? (
                <>
                    {groups.suggested.length > 0 && <GroupHeading text={SUGGESTED_GROUP}/>}
                    {groups.suggested.map(renderRow)}
                    {groups.catalog.length > 0 && <GroupHeading text={CATALOG_GROUP}/>}
                    {groups.catalog.map(renderRow)}
                </>
            ) : shown.map(renderRow)}
        </SuggestionsShell>
    );
};

interface IWizardBundlesTabProps {
    /** Every suggested bundle that was fetched. The search filters this whole set, not just the shown ones. */
    bundles: IStorefrontSuggestedBundle[];
    displayCount: number;
    /** The store the catalog search prices against. Without it there is nothing to search. */
    storefrontId?: string;
    loading: boolean;
    error: boolean;
    addedIds: string[];
    pendingId?: string;
    busy: boolean;
    onAdd: (bundle: IStorefrontSuggestedBundle) => void;
    onOpenBundle: (bundle: IStorefrontSuggestedBundle) => void;
}

/**
 * The bundle twin of the products tab.
 *
 * Two differences. Availability is a three-state answer: a bundle that cannot be priced for this store is a
 * different problem from one that is out of stock, and the amber state says so rather than collapsing both
 * into "Out of stock" and sending the operator to look at inventory. And the search is a plain text box,
 * because a bundle has a name and a price and nothing to compose a facet query out of.
 */
export const WizardBundlesTab: React.FC<IWizardBundlesTabProps> = (props) => {
    const [term, setTerm] = React.useState('');
    const [runSearch, results] = useLazySearchAdminStorefrontBundlesQuery();
    const searching = term.trim().length > 0;
    const {bundles, storefrontId} = props;

    React.useEffect(() => {
        const needle = term.trim();
        if (needle.length === 0 || !storefrontId) return;
        const handle = setTimeout(() => {
            // unlisted, because the point of the step is to put bundles on a store that does not carry
            // them yet. The rows are still priced for this storefront.
            runSearch({
                storefrontId,
                unlisted: true,
                conditions: {page: 1, pageSize: WIZARD_SEARCH_PAGE_SIZE, searchTerm: needle},
            });
        }, WIZARD_SEARCH_DEBOUNCE_MS);
        return () => clearTimeout(handle);
    }, [term, storefrontId, runSearch]);

    const groups = React.useMemo(
        () => partitionBundleResults(results.data?.items ?? [], filterSuggestedBundles(bundles, term)),
        [results.data, bundles, term]
    );
    const shown = React.useMemo(
        () => bundles.slice(0, props.displayCount).map(suggestedBundleRow),
        [bundles, props.displayCount]
    );

    const renderRow = (row: IWizardBundleRow) => (
        <SuggestionRow
            key={row.id}
            {...row}
            added={props.addedIds.includes(row.id)}
            pending={props.pendingId === row.id}
            disabled={props.busy}
            onOpen={() => props.onOpenBundle(row.addTarget)}
            onAdd={() => props.onAdd(row.addTarget)}
        />
    );

    const searchBar = (
        <SearchBox
            placeholder='Search your bundles'
            value={term}
            disabled={!props.storefrontId}
            onChange={(_, value) => setTerm(value ?? '')}
            onClear={() => setTerm('')}
            styles={{root: {width: '100%'}}}
        />
    );

    const searchEmpty = groups.suggested.length === 0 && groups.catalog.length === 0;
    return (
        <SuggestionsShell
            intro='Here are some suggested bundles for your Storefront. Search to add any other bundle you have built.'
            search={searchBar}
            loading={searching ? (results.isUninitialized || (results.isFetching && !results.data)) : props.loading}
            loadingLabel={searching ? 'Searching your bundles...' : 'Looking for bundles...'}
            error={searching ? results.isError : props.error}
            empty={searching ? searchEmpty : shown.length === 0}
            emptyMessage={searching
                ? 'None of your bundles matched that search.'
                : 'There are no bundles to suggest for this store yet.'}
        >
            {searching ? (
                <>
                    {groups.suggested.length > 0 && <GroupHeading text={SUGGESTED_GROUP}/>}
                    {groups.suggested.map(renderRow)}
                    {groups.catalog.length > 0 && <GroupHeading text={CATALOG_GROUP}/>}
                    {groups.catalog.map(renderRow)}
                </>
            ) : shown.map(renderRow)}
        </SuggestionsShell>
    );
};
